import React, { useState } from "react";
import { Paper, Typography, Box, styled, List, ListItem, Collapse, IconButton } from "@mui/material";
import InputBase from "@mui/material/InputBase";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import NavItem from "./NavItems";
import { useSelector } from "react-redux";
import { useTranslation } from 'react-i18next';

const StyledBox = styled(Box)(() => ({
  height: "100%",
  overflowY: "auto",
  overflowX: "hidden",
}));

const organizeItemsIntoCategories = (items = [], gid, uid) => {
  const categories = {};
  
  items.forEach((item) => {
    const hasAllowedGroupFilter = item.filters && 
      item.filters.some(filter => 
        filter.allowed_groups && 
        filter.allowed_groups.length > 0
      );

    const hasAllowedUserFilter = item.filters &&
      item.filters.some(filter =>
        filter.allowed_users &&
        filter.allowed_users.length > 0
      );
    
    if (!hasAllowedGroupFilter && !hasAllowedUserFilter) {
      if (!categories[item.category]) {
        categories[item.category] = [];
      }
      categories[item.category].push(item);
      return;
    }
    
    const isGroupAllowed = item.filters.some(filter => 
      filter.allowed_groups && 
      filter.allowed_groups.includes(gid)
    );

    const isUserAllowed = item.filters.some(filter =>
      filter.allowed_users &&
      filter.allowed_users.includes(uid)
    );
    
    if (isGroupAllowed || isUserAllowed) {
      if (!categories[item.category]) {
        categories[item.category] = [];
      }
      categories[item.category].push(item);
    }
  });
  
  return categories;
};

const getApiGatedCategories = (items = []) => {
  const gated = new Set();
  items.forEach(item => {
    if (item.filters?.some(f => f.use_api)) {
      gated.add(item.category);
    }
  });
  return gated;
};

const Nav = () => {
  const { t } = useTranslation();
  const route = useSelector((state) => state.route);
  let listItems = route.jsonData?.items || [];

  const categoryVisibility = route.categoryVisibility;
  const apiGatedCategories = getApiGatedCategories(listItems);
  const [filteredItems, setFilteredItems] = useState(listItems);
  const [openCategories, setOpenCategories] = useState(route.activeItem.category || null);

  const categorizedItems = organizeItemsIntoCategories(filteredItems, route.userGroupId, route.userId);

  const toggleCategory = (category) => {
    setOpenCategories(category === openCategories ? null : category);
  };

  const filter = (e) => {
    const keyword = e.target.value.toLowerCase();
    if (keyword !== "") {
      const results = listItems.filter((item) => item.title.toLowerCase().includes(keyword));
      setFilteredItems(results);
    } else {
      setFilteredItems(listItems);
    }
  };

  return (
    <Box
      sx={{
        padding: "24px 16px 24px 32px",
        boxShadow: "inset -1px 0px 0px rgba(0, 0, 0, 0.1)",
        borderRadius: "0",
        height: "inherit",
        background: "#FAFAFA",
        boxSizing: "border-box",
        position: "sticky",
        top: "0",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Typography variant="reportDisplay" component="div">
        {t('nav.title')}
      </Typography>
      <Typography variant="reportDisplayText" component="div" sx={{ mb: "10px" }}>
        {t('nav.subtitle')}
      </Typography>
      <Paper variant="outlined" component="form" sx={{ p: "8px 16px", display: "flex", alignItems: "center", m: "8px 0" }}>
        <IconButton type="submit" sx={{ p: 0, width: "14px" }} aria-label="search">
          <SearchIcon />
        </IconButton>
        <InputBase
          sx={{ ml: 1, flex: 1, fontSize: "14px", lineHeight: "24px" }}
          placeholder={t('nav.searchPlaceholder')}
          inputProps={{ "aria-label": t('nav.searchPlaceholder') }}
          onChange={filter}
          type="search"
          className="input"
        />
      </Paper>
      <StyledBox>
        {Object.keys(categorizedItems).length > 0 ? (
          Object.entries(categorizedItems)
          .filter(([category]) => 
            !apiGatedCategories.has(category) || categoryVisibility[category] === true
          )
          .map(([category, items]) => (
            <Box key={category} sx={{ mt: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  cursor: "pointer",
                  p: 1,
                  background: "#e0e0e0",
                  borderRadius: "4px",
                }}
                onClick={() => toggleCategory(category)}
              >
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                  {category}
                </Typography>
                <IconButton size="small">
                  {openCategories === category ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                </IconButton>
              </Box>

              <Collapse in={openCategories === category} timeout="auto" unmountOnExit>
                <List>
                  {items.map((item, i) => (
                    <ListItem key={i} disablePadding>
                      <NavItem item={item} />
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            </Box>
          ))
        ) : (
          <Typography variant="h6">{t('nav.noResults')}</Typography>
        )}
      </StyledBox>
    </Box>
  );
};

export default Nav;
